import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useSelector } from "react-redux";
import Select from "react-select";
import Swal from "sweetalert2";
import "./AlertFormEdit.css";
import { useIntl } from "react-intl";
import { Toast } from "../../../helpers/alert";
import useFilteredUsersByDevice from "../../../hooks/useFilteredUsersByDevice";
import { publicRequest } from "../../../requestMeth";

const MaximumDemandAlertFormEdit = ({
  devices,
  users,
  onCancel,
  onSave,
  initialData,
  userPrivilege,
}) => {
  const userToken = useSelector((state) => state.user.currentUser?.accessToken);
  const intl = useIntl();

  const normalizeKey = useCallback((key) => {
    if (!key) return "";
    return String(key).trim().toUpperCase();
  }, []);

  const isIndexedKey = useCallback((key) => {
    return /^(T|P)\d+$/i.test(String(key || "").trim());
  }, []);

  const getNumericIndex = useCallback((key) => {
    const match = String(key || "")
      .trim()
      .match(/\d+/);
    return match ? Number(match[0]) : Number.MAX_SAFE_INTEGER;
  }, []);

  const orderIndexedKeys = useCallback(
    (keys) => {
      return [...keys].sort((a, b) => getNumericIndex(a) - getNumericIndex(b));
    },
    [getNumericIndex]
  );

  const getVisibleBandCodes = useCallback(
    (bands) => {
      return (bands || [])
        .map((band) => normalizeKey(band?.code))
        .filter(Boolean);
    },
    [normalizeKey]
  );

  const hasVisibleValue = useCallback((value) => {
    return value !== null && value !== undefined && String(value).trim() !== "";
  }, []);

  const resolveContractedPowerByBands = useCallback(
    (rawMap, bands) => {
      const source = rawMap && typeof rawMap === "object" ? { ...rawMap } : {};
      const normalizedEntries = Object.entries(source).map(([key, value]) => [
        normalizeKey(key),
        value,
      ]);

      const visibleCodes = getVisibleBandCodes(bands);
      const result = {};

      visibleCodes.forEach((code) => {
        result[code] = "";
      });

      normalizedEntries.forEach(([key, value]) => {
        if (visibleCodes.includes(key)) {
          result[key] = value ?? "";
        }
      });

      const indexedEntries = normalizedEntries.filter(([key]) =>
        isIndexedKey(key)
      );

      const directEntries = normalizedEntries.filter(
        ([key]) => !isIndexedKey(key)
      );

      if (indexedEntries.length > 0) {
        const orderedIndexed = orderIndexedKeys(
          indexedEntries.map(([key]) => key)
        );

        orderedIndexed.forEach((indexedKey, idx) => {
          const targetBand = visibleCodes[idx];
          if (!targetBand) return;

          const value =
            source[indexedKey] ?? source[indexedKey.toLowerCase()] ?? "";

          if (result[targetBand] === "" || result[targetBand] === null) {
            result[targetBand] = value ?? "";
          }
        });
      }

      directEntries.forEach(([key, value]) => {
        if (visibleCodes.includes(key)) {
          result[key] = value ?? "";
        }
      });

      return result;
    },
    [getVisibleBandCodes, isIndexedKey, normalizeKey, orderIndexedKeys]
  );

  const toEmailVal = useCallback(
    (arr) => {
      if (!Array.isArray(arr)) return [];

      const byId = new Map((users || []).map((u) => [Number(u.id), u]));
      const byEmail = new Map(
        (users || []).map((u) => [String(u.email || ""), u])
      );

      return arr
        .map((v) => {
          if (
            typeof v === "number" ||
            (typeof v === "string" && /^\d+$/.test(v))
          ) {
            const u = byId.get(Number(v));
            return u?.email ? { value: u.id, label: u.email } : null;
          }

          if (typeof v === "string") {
            const u = byEmail.get(v);
            return u?.email ? { value: u.id, label: u.email } : null;
          }

          return null;
        })
        .filter(Boolean);
    },
    [users]
  );

  const toPhoneVal = useCallback(
    (arr) => {
      if (!Array.isArray(arr)) return [];

      const byId = new Map((users || []).map((u) => [Number(u.id), u]));
      const byPhone = new Map(
        (users || []).map((u) => [String(u.phone || ""), u])
      );

      return arr
        .map((v) => {
          if (
            typeof v === "number" ||
            (typeof v === "string" && /^\d+$/.test(v))
          ) {
            const u = byId.get(Number(v));
            return u?.phone ? { value: u.id, label: u.phone } : null;
          }

          if (typeof v === "string") {
            const u = byPhone.get(v);
            return u?.phone ? { value: u.id, label: u.phone } : null;
          }

          return null;
        })
        .filter(Boolean);
    },
    [users]
  );

  const resolveInitialDevice = useCallback(() => {
    const dev0 = initialData?.config?.devices?.[0];
    const devId =
      typeof dev0 === "object" ? dev0?.device_id ?? dev0?.id : dev0;

    return devId
      ? (devices || []).find((d) => d.value === devId) || null
      : null;
  }, [initialData, devices]);

  const [timebands, setTimebands] = useState([]);
  const [intervalMinutes, setIntervalMinutes] = useState(null);
  const [loadingTimebands, setLoadingTimebands] = useState(false);

  const [formData, setFormData] = useState(() => {
    const freq = initialData?.config?.frequency || {};
    const rt = freq?.realtime || {};
    const daily = freq?.daily || {};
    const weekly = freq?.weekly || {};
    const monthly = freq?.monthly || {};

    return {
      name: initialData?.name || "",
      device: resolveInitialDevice(),
      contractedPower: {},
      tariff: initialData?.config?.tariff || "",
      active: initialData?.active === 1 || initialData?.active === true,
      realtimeEmails: toEmailVal(rt.emails),
      realtimePhones: toPhoneVal(rt.phones),
      dailyEmails: toEmailVal(daily.emails),
      weeklyEmails: toEmailVal(weekly.emails),
      monthlyEmails: toEmailVal(monthly.emails),
      language: initialData?.config?.language || "es",
    };
  });

  const [errors, setErrors] = useState({});
  const [isValid, setIsValid] = useState(false);
  const lastInitKeyRef = useRef(null);
  const [isDirty, setIsDirty] = useState(false);

  const initKey = useMemo(
    () =>
      initialData?.id ??
      initialData?.uuid ??
      initialData?.name ??
      JSON.stringify({
        devices: initialData?.config?.devices,
        contractedPower: initialData?.config?.contractedPower,
        tariff: initialData?.config?.tariff,
        frequency: initialData?.config?.frequency,
        language: initialData?.config?.language,
        name: initialData?.name,
        active: initialData?.active,
      }),
    [initialData]
  );

  const buildStateFromInitial = useCallback(
    (bands) => {
      if (!initialData) return null;

      const freq = initialData?.config?.frequency || {};
      const rt = freq?.realtime || {};
      const daily = freq?.daily || {};
      const weekly = freq?.weekly || {};
      const monthly = freq?.monthly || {};

      return {
        name: initialData?.name || "",
        device: resolveInitialDevice(),
        contractedPower: resolveContractedPowerByBands(
          initialData?.config?.contractedPower || {},
          bands || []
        ),
        tariff: initialData?.config?.tariff || "",
        active: initialData?.active === 1 || initialData?.active === true,
        realtimeEmails: toEmailVal(rt.emails),
        realtimePhones: toPhoneVal(rt.phones),
        dailyEmails: toEmailVal(daily.emails),
        weeklyEmails: toEmailVal(weekly.emails),
        monthlyEmails: toEmailVal(monthly.emails),
        language: initialData?.config?.language || "es",
      };
    },
    [
      initialData,
      resolveInitialDevice,
      resolveContractedPowerByBands,
      toEmailVal,
      toPhoneVal,
    ]
  );

  useEffect(() => {
    const deviceId = resolveInitialDevice()?.value;

    if (!deviceId || !userToken) {
      setTimebands([]);
      setIntervalMinutes(null);
      setFormData((prev) => ({
        ...prev,
        contractedPower: initialData?.config?.contractedPower || {},
        tariff: initialData?.config?.tariff || "",
      }));
      return;
    }

    const fetchTimebands = async () => {
      setLoadingTimebands(true);

      try {
        const res = await publicRequest.get(`/maximeter/config-for-device`, {
          headers: { token: `Bearer ${userToken}` },
          params: { deviceId },
        });

        const timebandsData = res.data || {};
        const nextTimebands = timebandsData?.timebands || [];
        const nextInterval = timebandsData?.interval_minutes ?? null;

        const nextTariff =
          timebandsData?.tariff_structure ??
          timebandsData?.tariff ??
          timebandsData?.tariff_name ??
          "";

        setTimebands(nextTimebands);
        setIntervalMinutes(nextInterval);

        setFormData((prev) => ({
          ...prev,
          contractedPower: resolveContractedPowerByBands(
            initialData?.config?.contractedPower || {},
            nextTimebands
          ),
          tariff: nextTariff || prev.tariff || "",
        }));
      } catch (error) {
        console.error("[MaximumDemandAlertFormEdit] Error fetching config:", error);

        setTimebands([]);
        setIntervalMinutes(null);
        setFormData((prev) => ({
          ...prev,
          contractedPower: initialData?.config?.contractedPower || {},
          tariff: prev.tariff || initialData?.config?.tariff || "",
        }));
      } finally {
        setLoadingTimebands(false);
      }
    };

    fetchTimebands();
  }, [
    initialData,
    resolveInitialDevice,
    resolveContractedPowerByBands,
    userToken,
  ]);

  useEffect(() => {
    if (
      !initialData ||
      !(Array.isArray(users) && users.length) ||
      !(Array.isArray(devices) && devices.length)
    ) {
      return;
    }

    const sameInit = lastInitKeyRef.current === initKey;
    if (isDirty && sameInit) return;

    const next = buildStateFromInitial(timebands);
    if (!next) return;

    setFormData((prev) => {
      const simplify = (s) => ({
        ...s,
        device: s.device?.value ?? null,
        contractedPower: s.contractedPower || {},
        realtimeEmails: (s.realtimeEmails || []).map((x) => x.value).sort(),
        realtimePhones: (s.realtimePhones || []).map((x) => x.value).sort(),
        dailyEmails: (s.dailyEmails || []).map((x) => x.value).sort(),
        weeklyEmails: (s.weeklyEmails || []).map((x) => x.value).sort(),
        monthlyEmails: (s.monthlyEmails || []).map((x) => x.value).sort(),
      });

      return JSON.stringify(simplify(prev)) === JSON.stringify(simplify(next))
        ? prev
        : { ...prev, ...next };
    });

    lastInitKeyRef.current = initKey;
    setIsDirty(false);
  }, [
    initKey,
    initialData,
    users,
    devices,
    buildStateFromInitial,
    isDirty,
    timebands,
  ]);

  const filteredUsers = useFilteredUsersByDevice(
    formData.device ? [formData.device] : [],
    users,
    userPrivilege
  );

  const emailOptions = useMemo(
    () =>
      (filteredUsers || [])
        .filter((u) => !!u?.email)
        .map((u) => ({ value: u.id, label: u.email })),
    [filteredUsers]
  );

  const visibleBandCodes = useMemo(() => {
    const allCodes = getVisibleBandCodes(timebands);

    return allCodes.filter((code) =>
      hasVisibleValue(formData.contractedPower?.[code])
    );
  }, [
    timebands,
    getVisibleBandCodes,
    formData.contractedPower,
    hasVisibleValue,
  ]);

  const handleChange = (field, value) => {
    setIsDirty(true);

    if (field === "device") {
      setFormData((prev) => ({
        ...prev,
        device: value,
        tariff: "",
        realtimeEmails: [],
        realtimePhones: [],
        dailyEmails: [],
        weeklyEmails: [],
        monthlyEmails: [],
        contractedPower: {},
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePowerChange = (period, value) => {
    const normalized = normalizeKey(period);

    setIsDirty(true);
    setFormData((prev) => ({
      ...prev,
      contractedPower: {
        ...prev.contractedPower,
        [normalized]: value,
      },
    }));
  };

  useEffect(() => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "El nombre es obligatorio";
    }

    if (!formData.device) {
      newErrors.device = "Debe seleccionar un dispositivo";
    }

    const powerValues = Object.values(formData.contractedPower || {});
    const hasValidPower = powerValues.some(
      (v) => v !== "" && v !== null && v !== undefined && Number(v) > 0
    );

    if (!hasValidPower) {
      newErrors.contractedPower =
        "Debe ingresar al menos una potencia contratada válida";
    }

    if (!String(formData.tariff || "").trim()) {
      newErrors.tariff = "No se encontró la estructura tarifaria del dispositivo";
    }

    setErrors(newErrors);
    setIsValid(Object.keys(newErrors).length === 0);
  }, [formData]);

  const translateDay = useCallback((day) => {
    const map = {
      monday: "Lunes",
      tuesday: "Martes",
      wednesday: "Miércoles",
      thursday: "Jueves",
      friday: "Viernes",
      saturday: "Sábado",
      sunday: "Domingo",
      mon: "Lunes",
      tue: "Martes",
      wed: "Miércoles",
      thu: "Jueves",
      fri: "Viernes",
      sat: "Sábado",
      sun: "Domingo",
    };

    const normalized = String(day || "").trim().toLowerCase();
    return map[normalized] || day;
  }, []);

  const formatHour = useCallback((value) => {
    if (!value) return "";
    return String(value).slice(0, 5);
  }, []);

  const formatSegment = (segment) => {
    if (!segment) return "";

    const days =
      segment.days && Array.isArray(segment.days)
        ? segment.days.map(translateDay).join(", ")
        : "";

    const from = formatHour(segment.from);
    const to = formatHour(segment.to);

    if (!days && !from && !to) return "";
    if (days && from && to) return `${days}: ${from} - ${to}`;
    if (days) return days;
    if (from && to) return `${from} - ${to}`;

    return "";
  };

  const buildTooltip = (band) => {
    if (!band || !Array.isArray(band.segments) || band.segments.length === 0) {
      return "";
    }

    return band.segments.map(formatSegment).join("\n");
  };

  const handleSubmit = () => {
    Swal.fire({
      title: "¿Desea guardar los cambios de la alerta?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: intl.formatMessage({
        id: "confirm.yes",
        defaultMessage: "Sí, continuar.",
      }),
      cancelButtonText: intl.formatMessage({
        id: "confirm.no",
        defaultMessage: "Cancelar",
      }),
    }).then((result) => {
      if (result.isConfirmed && isValid) {
        const emailIds = (arr) =>
          (arr || [])
            .map((e) => e.value)
            .filter((v) => v !== undefined && v !== null);

        const phonesFromIds = (arr) =>
          (arr || [])
            .map((p) => p.value)
            .filter((v) => v !== undefined && v !== null);

        const payload = {
          type: "MAXIMETRO",
          name: formData.name?.toUpperCase(),
          active: formData.active ? 1 : 0,
          config: {
            devices: formData.device?.value ? [formData.device.value] : [],
            contractedPower: { ...formData.contractedPower },
            tariff: formData.tariff,
            language: formData.language,
            frequency: {
              realtime: {
                enabled:
                  (formData.realtimeEmails?.length || 0) > 0 ||
                  (formData.realtimePhones?.length || 0) > 0,
                rateLimit: null,
                emails: emailIds(formData.realtimeEmails),
                phones: phonesFromIds(formData.realtimePhones),
              },
              daily: {
                enabled: (formData.dailyEmails?.length || 0) > 0,
                time: "09:00",
                emails: emailIds(formData.dailyEmails),
                phones: [],
              },
              weekly: {
                enabled: (formData.weeklyEmails?.length || 0) > 0,
                day: "monday",
                time: "09:00",
                emails: emailIds(formData.weeklyEmails),
                phones: [],
              },
              monthly: {
                enabled: (formData.monthlyEmails?.length || 0) > 0,
                day: 1,
                time: "09:00",
                emails: emailIds(formData.monthlyEmails),
                phones: [],
              },
            },
          },
        };

        onSave(payload);
      } else if (!isValid) {
        Toast.fire({
          icon: "info",
          title: intl.formatMessage(
            {
              id: "error.saveMaximetro",
              defaultMessage: "No se pudo guardar la alerta: {errors}",
            },
            { errors: Object.values(errors).join(", ") }
          ),
        });
      }
    });
  };

  return (
    <div className="maximetro-form">
      <h3 className="maximetro-title">Editar alerta: Exceso de máxímetro</h3>

      <label className="maximetro-label">Nombre de la alerta</label>
      <input
        type="text"
        className="maximetro-input"
        value={formData.name}
        onChange={(e) => handleChange("name", e.target.value)}
        placeholder="Ej: Alerta de maximetro"
      />
      {errors.name && <small className="error-text">{errors.name}</small>}

      <label className="maximetro-label">Dispositivo</label>
      <Select
        options={devices}
        value={formData.device}
        onChange={(val) => handleChange("device", val)}
        placeholder="Selecciona un dispositivo"
      />
      {errors.device && <small className="error-text">{errors.device}</small>}

      <label className="maximetro-label">Potencia contratada (kW)</label>
      {intervalMinutes ? (
        <p className="maximetro-note">
          Intervalo de integración: {intervalMinutes} minutos
        </p>
      ) : null}

      {loadingTimebands ? <p>Cargando potencias contratadas...</p> : null}

      <div className="maximetro-inline">
        {visibleBandCodes.map((code) => {
          const band = (timebands || []).find(
            (item) => normalizeKey(item?.code) === code
          );
          const tooltip = buildTooltip(band);

          return (
            <div
              key={code}
              style={{ display: "flex", flexDirection: "column", gap: "4px" }}
            >
              <label className="maximetro-timeband-label">
                <span>{code}</span>
                {tooltip ? (
                  <span
                    className="maximetro-timeband-info"
                    title={tooltip}
                    aria-label={`Información de la banda ${code}`}
                  >
                    i
                  </span>
                ) : null}
              </label>
              <input
                type="number"
                className="maximetro-input small"
                placeholder={code}
                value={formData.contractedPower?.[code] ?? ""}
                onChange={(e) => handlePowerChange(code, e.target.value)}
              />
            </div>
          );
        })}
      </div>

      {errors.contractedPower && (
        <small className="error-text">{errors.contractedPower}</small>
      )}

      <label className="maximetro-label">Estructura tarifaria</label>
      <input
        type="text"
        className="maximetro-input"
        value={formData.tariff || ""}
        readOnly
        placeholder={
          formData.device
            ? "Estructura tarifaria no disponible"
            : "Seleccione un dispositivo"
        }
      />
      {errors.tariff && <small className="error-text">{errors.tariff}</small>}

      <label className="maximetro-label">¿Activar?</label>
      <div className="maximetro-inline">
        <label>
          <input
            type="radio"
            checked={!!formData.active}
            onChange={() => handleChange("active", true)}
          />{" "}
          Sí
        </label>
        <label>
          <input
            type="radio"
            checked={!formData.active}
            onChange={() => handleChange("active", false)}
          />{" "}
          No
        </label>
      </div>

      <h4 className="maximetro-subtitle">Notificaciones</h4>

      <div className="maximetro-inline">
        Tiempo real (email):
        <Select
          isMulti
          className="maximetro-select"
          options={emailOptions}
          value={formData.realtimeEmails}
          onChange={(val) => handleChange("realtimeEmails", val || [])}
          placeholder="Selecciona usuarios"
        />
      </div>

      <h4 className="maximetro-subtitle">Resúmenes</h4>

      <label>Informe diario</label>
      <Select
        isMulti
        className="maximetro-select"
        options={emailOptions}
        value={formData.dailyEmails}
        onChange={(val) => handleChange("dailyEmails", val || [])}
        placeholder="Selecciona usuarios"
      />

      <label>Informe semanal</label>
      <Select
        isMulti
        className="maximetro-select"
        options={emailOptions}
        value={formData.weeklyEmails}
        onChange={(val) => handleChange("weeklyEmails", val || [])}
        placeholder="Selecciona usuarios"
      />

      <label>Informe mensual</label>
      <Select
        isMulti
        className="maximetro-select"
        options={emailOptions}
        value={formData.monthlyEmails}
        onChange={(val) => handleChange("monthlyEmails", val || [])}
        placeholder="Selecciona usuarios"
      />

      <label className="maximetro-label">Idioma</label>
      <select
        className="maximetro-select"
        value={formData.language}
        onChange={(e) => handleChange("language", e.target.value)}
      >
        <option value="es">Español</option>
        <option value="en">Inglés</option>
      </select>

      <div className="maximetro-actions">
        <button
          className="maximetro-btn-save"
          onClick={handleSubmit}
          disabled={!isValid}
        >
          Guardar
        </button>
        <button className="maximetro-btn-cancel" onClick={onCancel}>
          Cancelar
        </button>
      </div>
    </div>
  );
};

export default MaximumDemandAlertFormEdit;